import HomeCard from '@components/cards/HomeCard';
import ProductCard from '@components/cards/ProductCard';
import awaitUserLogin from '@components/login/awaitUserLogin';
import SearchInput from '@components/search-input/SearchInput';
import UserContext from '@context/UserContext';
import useFacetSearch from '@hooks/useFacetSearch';
import Sentry from '@integrations/Sentry';
import { ContentBlock } from '@layouts/ContentBlocks';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import MuiPagination from '@mui/material/Pagination';
import Typography from '@mui/material/Typography';
import useBreakpoints from '@ui/hooks/useBreakpoints';
import onSavedClickProduct from '@utils/clickHandlers/onSavedClickProduct';
import { addSavedHome, removeSavedHome } from '@utils/savedItems';
import { homeToSegmentEventData, productToSegmentEventData } from '@utils/segmentAnalytics';
import { makeSeoData } from 'config/next-seo.config';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { NextSeo } from 'next-seo';
import React, { useContext, useEffect } from 'react';

import useStyles from './search.style';

const title = 'Search | Guest House';
const description = '';

export const emptyInitResults = {
  hits: [],
  page: 0,
  nbHits: 0,
  nbPages: 0,
  hitsPerPage: 0,
  processingTimeMS: 0,
  query: '',
  params: '',
  exhaustiveNbHits: false,
  useRouter: true,
};


function Dibs() {
  const router = useRouter();
  const { theme } = useStyles();
  const userContext = useContext<UserContext>(UserContext);
  const { md } = useBreakpoints();

  const {
    results: productsResults,
    query,
    setQuery: setProductQuery,
    page: productsPage,
    setPage: setProductsPage,
  } = useFacetSearch({
    query: router.query,
    initResults: emptyInitResults,
    hitsPerPage: 12,
  });

  const {
    results: homesResults,
    setQuery: setHomesQuery,
    page: homesPage,
    setPage: setHomesPage,
  } = useFacetSearch<guesthouse.Home>({
    query: router.query,
    initResults: emptyInitResults,
    syncUrl: false,
    indexOptions: {
      init: 'homes',
      offer_price_desc: 'homes',
      offer_price_asc: 'homes',
    },
    extraFacetFilters: {
      inStock: undefined,
    },
    hitsPerPage: 6,
  });

  useEffect(() => {
    if (!router.isReady) {
      return;
    }

    if (Object.keys(router.query).includes('page')) {
      setProductsPage(Number(router.query['page']));
    }
  }, [router.isReady]);

  const hasResults = !!homesResults?.hits?.length || !!productsResults?.hits?.length;

  return (
    <>
      <Head>
        <title>
          {title}
        </title>
      </Head>

      <NextSeo {...makeSeoData({ pageTitle: title, metaTitle: title, metaDescription: description })} />

      <ContentBlock
        margin={[theme.spacing(10, 0, 5), 0]}
        style={{
          maxWidth: 600,
        }}
      >
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <SearchInput
            autoFocus
            placeholder="Search for homes or products"
            value={query}
            style={{
              height: 50,
            }}
            onValueChange={(value) => {
              setProductQuery(value || '');
              setHomesQuery(value || '');
              setProductsPage(0);
              setHomesPage(0);
            }}
          />
        </Box>
      </ContentBlock>

      {
        (!hasResults && query)
          ? (
            <ContentBlock>
              <Typography
                textAlign="center"
                style={{
                  fontFamily: theme.gh_vars.circular,
                }}
              >
                Sorry, we couldn&apos;t find any matching results for this search
              </Typography>
            </ContentBlock>
          ) : (
            <>
              {
                !!homesResults.hits?.length && query
                && (
                  <ContentBlock
                    margin={[2, 0, 12]}
                    marginMd={[6, 0, 12]}
                  >
                    <Grid
                      container
                      spacing={4}
                    >
                      <Grid
                        item
                        xs={12}
                        style={{
                          paddingTop: 0,
                          position: 'relative'
                        }}
                      >
                        <div
                          style={{
                            display: 'flex',
                            flexDirection: md ? 'row' : 'column',
                            justifyContent: md ? 'space-between' : 'center',
                            margin: 'auto'
                          }}
                        >
                          {homesResults?.page + 1 === homesResults?.nbPages ?
                            <Typography style={{ margin: theme.spacing(3, 0), fontSize: 14 }}>
                              Showing
                              {' '}
                              {homesResults.page === 0 ?
                                homesResults.nbHits - (homesResults.nbHits - 1)
                                :
                                homesResults.nbHits - (homesResults.nbHits - (homesResults.hitsPerPage * homesResults.page))
                              }
                              {' '}
                              -
                              {' '}
                              {homesResults.nbHits}
                              {' '}
                              of
                              {' '}
                              {homesResults.nbHits}
                              {' '}
                              home
                              {`${homesResults.hitsPerPage !== 1 && 's'}`}
                            </Typography>
                            :
                            <Typography style={{ margin: theme.spacing(3, 0), fontSize: 14 }}>
                              Showing
                              {' '}
                              {homesResults.hitsPerPage * (homesResults.page + 1) - (homesResults.hitsPerPage - 1)}
                              {' '}
                              -
                              {' '}
                              {homesResults.hitsPerPage * (homesResults.page + 1)}
                              {' '}
                              of
                              {' '}
                              {homesResults.nbHits}
                              {' '}
                              home
                              {`${homesResults.hitsPerPage !== 1 && 's'}`}
                            </Typography>
                          }
                          {homesResults.nbPages > 1 &&
                            <div>
                              <MuiPagination
                                size={md ? 'medium' : 'small'}
                                count={homesResults.nbPages}
                                page={homesPage === 0 ? 1 : homesPage}
                                style={{ margin: theme.spacing(3) }}
                                onChange={(e, value: number) => {
                                  e.preventDefault();
                                  setHomesPage(value);
                                }}
                              />
                            </div>
                          }
                        </div>

                        <Grid
                          container
                          spacing={1.5}
                        >
                          {homesResults?.hits?.length > 0 && homesResults.hits.map((home) => {
                            const saved = userContext.savedHomes?.ids.indexOf(home.docID) > -1;

                            return (
                              <Grid
                                key={home.docID}
                                item
                                xs={6}
                                md={4}
                              >
                                <HomeCard
                                  home={home}
                                  saved={saved}
                                  onClick={() => {
                                    try {
                                      window.analytics?.track('Home Clicked', homeToSegmentEventData(home));
                                    } catch (e) {
                                      Sentry.captureException(e);
                                    }
                                  }}
                                  onSavedClick={async (e) => {
                                    e.stopPropagation();

                                    if (userContext.user) {
                                      if (saved) {
                                        removeSavedHome(userContext.user.uid, home);
                                      } else {
                                        addSavedHome(userContext.user.uid, home);
                                      }
                                    } else {
                                      await awaitUserLogin(userContext)
                                        .then((user) => {
                                          addSavedHome(user.uid, home);
                                        });
                                    }
                                  }}
                                />
                              </Grid>
                            );
                          })}
                        </Grid>

                      </Grid>
                    </Grid>

                    {homesResults.nbPages > 1 &&
                      <div>
                        <MuiPagination
                          size={md ? 'medium' : 'small'}
                          count={homesResults.nbPages}
                          page={homesPage === 0 ? 1 : homesPage}
                          style={{ margin: theme.spacing(3) }}
                          onChange={(e, value: number) => {
                            e.preventDefault();
                            setHomesPage(value);
                          }}
                        />
                      </div>
                    }
                  </ContentBlock>
                )
              }


              {
                !!productsResults.hits?.length && query
                && (
                  <ContentBlock
                    margin={[2, 0, 12]}
                    marginMd={[6, 0, 12]}
                  >
                    <Grid
                      container
                      spacing={4}
                    >
                      <Grid
                        item
                        xs={12}
                        style={{
                          paddingTop: 0,
                          position: 'relative'
                        }}
                      >
                        <div
                          style={{
                            display: 'flex',
                            flexDirection: md ? 'row' : 'column',
                            justifyContent: md ? 'space-between' : 'center',
                            margin: 'auto'
                          }}
                        >
                          {productsResults?.page + 1 === productsResults?.nbPages ?
                            <Typography style={{ margin: theme.spacing(3, 0), fontSize: 14 }}>
                              Showing
                              {' '}
                              {productsResults.page === 0 ?
                                productsResults.nbHits - (productsResults.nbHits - 1)
                                :
                                productsResults.nbHits - (productsResults.nbHits - (productsResults.hitsPerPage * productsResults.page))
                              }
                              {' '}
                              -
                              {' '}
                              {productsResults.nbHits}
                              {' '}
                              of
                              {' '}
                              {productsResults.nbHits}
                              {' '}
                              product
                              {`${productsResults.hitsPerPage !== 1 && 's'}`}
                            </Typography>
                            :
                            <Typography style={{ margin: theme.spacing(3, 0), fontSize: 14 }}>
                              Showing
                              {' '}
                              {productsResults.hitsPerPage * (productsResults.page + 1) - (productsResults.hitsPerPage - 1)}
                              {' '}
                              -
                              {' '}
                              {productsResults.hitsPerPage * (productsResults.page + 1)}
                              {' '}
                              of
                              {' '}
                              {productsResults.nbHits}
                              {' '}
                              product
                              {`${productsResults.hitsPerPage !== 1 && 's'}`}
                            </Typography>
                          }
                          {productsResults.nbPages > 1 &&
                            <div>
                              <MuiPagination
                                size={md ? 'medium' : 'small'}
                                count={productsResults.nbPages}
                                page={productsPage === 0 ? 1 : productsPage}
                                style={{ margin: theme.spacing(3) }}
                                onChange={(e, value: number) => {
                                  e.preventDefault();
                                  setProductsPage(value);
                                }}
                              />
                            </div>
                          }
                        </div>

                        <Grid
                          container
                          spacing={1.5}
                        >
                          {productsResults?.hits?.length > 0 && productsResults.hits.map((product) => {
                            const saved = userContext.savedProducts?.ids.indexOf(product.docID) > -1;

                            return (
                              <Grid
                                key={product.docID}
                                item
                                xs={6}
                                md={3}
                                lg={2}
                              >
                                <ProductCard
                                  product={product}
                                  saved={saved}
                                  onClick={() => {
                                    try {
                                      window.analytics?.track('Product Clicked', productToSegmentEventData(product));
                                    } catch (e) {
                                      Sentry.captureException(e);
                                    }
                                  }}
                                  onSavedClick={async (e) => onSavedClickProduct(userContext, saved, product as guesthouse.Product, e)}
                                />
                              </Grid>
                            );
                          })}
                        </Grid>

                      </Grid>
                    </Grid>

                    {productsResults.nbPages > 1 &&
                      <div>
                        <MuiPagination
                          size={md ? 'medium' : 'small'}
                          count={productsResults.nbPages}
                          page={productsPage === 0 ? 1 : productsPage}
                          style={{ margin: theme.spacing(3) }}
                          onChange={(e, value: number) => {
                            e.preventDefault();
                            setProductsPage(value);
                          }}
                        />
                      </div>
                    }
                  </ContentBlock>
                )
              }
            </>
          )
      }
    </>
  );
}

export default Dibs;
